import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  EducationFormFields,
  type EducationFormData,
} from '@/features/educations/components/EducationFormFields';
import {
  DEFAULT_VALUES,
  toFormValues,
  buildRequest,
  getLabels,
  resetFormState,
} from '@/features/educations/components/EducationFormDialog.utils';
import { useCandidateEducations } from '../hooks/useCandidateEducations';
import type { Education } from '@/features/educations/types/education.types';

interface CandidateEducationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  education?: Education | null;
}

export function CandidateEducationFormDialog({
  open,
  onOpenChange,
  mode,
  education,
}: CandidateEducationFormDialogProps): JSX.Element {
  const { createEducation, updateEducation } = useCandidateEducations();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EducationFormData>({
    defaultValues: mode === 'edit' && education ? toFormValues(education) : DEFAULT_VALUES,
  });

  useEffect(() => resetFormState(reset, mode, education), [education, mode, reset]);

  const handleClose = (): void => onOpenChange(false);

  const onSubmit = handleSubmit((data) => {
    const request = buildRequest(data);
    if (mode === 'create') createEducation(request);
    else if (education) updateEducation(education.id, request);
    handleClose();
  });

  const { title, submitLabel } = getLabels(mode, false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <EducationFormFields
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
